// noinspection JSUnresolvedVariable

'use strict';

/**
 * @ngdoc service
 * @name 360StoreAngularApp.api
 * @description
 * # api
 * Service in the 360StoreAngularApp.
 */
angular.module('360StoreAngularApp')
	.service('api', function ($q, $http, $rootScope, $location, BASE_URL) {
		let lastLoginWarning = -1;

		function unwrapResponse(response) {
			return response.data;
		}

		function handleError(err) {
			console.error(err);
			const errorOverride = err && err.headers && err.headers() && err.headers().proscerror;
			if (errorOverride) {
				toastr.warning(errorOverride);
			} else if (err.status === 401) {
				if (lastLoginWarning < Date.now() - 5000) { // don't spam this warning
					toastr.warning('You must log in to access this section');
					lastLoginWarning = Date.now();
				}
				if ($location.path().indexOf('login') === -1) {
					$rootScope.savedRoute = {path: $location.path(), search: $location.search() || ''};
					$rootScope.postLoginPath = $location.path();
					if ($location.path().includes('my-licenses')) {
						$location.path('/login-customer'); // magic link login form
					} else {
						$location.path('/login');
					}
				}
			} else if (err.status === 403) {
				toastr.warning('Your account does not permit access to this section. You can <a href="/logout">Log Out Here</a> then log back in as a different user', 'Access Denied');
			} else if (err.status === 404) {
				toastr.error('Bad request (404)');
			} else if (err.status <= 0) {
				toastr.warning('The server is not responding, please try again in a minute');
			} else {
				toastr.error('Unexpected error from server (' + err.status + ')');
			}
			throw err;
		}

		$rootScope.requestInProgress = 0;

		function updateOrClearLocalStorage(licenseKey, registeredTo, rememberMe) {
			if (rememberMe) {
				localStorage.setItem('licenseKey', licenseKey);
				localStorage.setItem('registeredTo', registeredTo);
			} else {
				localStorage.removeItem('licenseKey');
				localStorage.removeItem('registeredTo');
			}
		}

		return {

			/*
			///////////////////////////////////////////////////////////////////////////
			// INTERNAL FUNCTIONS
			// Generally these should not be called directly, use a specific named
			// function instead
			///////////////////////////////////////////////////////////////////////////
			*/
			get: function (url, config) {
				$rootScope.requestInProgress += 1;
				return $http.get(BASE_URL + url, config).then(unwrapResponse, handleError)
					.finally(function () {
						$rootScope.requestInProgress -= 1;
					});
			},

			post: function (url, payload, config) {
				$rootScope.requestInProgress += 1;
				return $http.post(BASE_URL + url, payload, config).then(unwrapResponse, handleError)
					.finally(function () {
						$rootScope.requestInProgress -= 1;
					});
			},

			put: function (url, payload, config) {
				$rootScope.requestInProgress += 1;
				return $http.put(BASE_URL + url, payload, config).then(unwrapResponse, handleError)
					.finally(function () {
						$rootScope.requestInProgress -= 1;
					});
			},

			del: function (url, config) {
				$rootScope.requestInProgress += 1;
				return $http.delete(BASE_URL + url, config).then(unwrapResponse, handleError)
					.finally(function () {
						$rootScope.requestInProgress -= 1;
					});
			},

			/*
			///////////////////////////////////////////////////////////////////////////
			// CUSTOM FUNCTIONS
			///////////////////////////////////////////////////////////////////////////
			*/

			/**
			 * @param action {CartAction | Array<CartAction>}
			 * @return {*}
			 */
			createCartAction: function (action) {
				if (angular.isArray(action)) { // batch
					return this.post('api/add-cart-action-batch', action);
				} else {
					return this.post('api/add-cart-action', action);
				}
			},

			/**
			 * @param payload {CartAction}
			 * @return {Promise<CartAction>}
			 */
			addPortfolioRebate: function (payload) {
				return this.post('api/portfolio-rebate', payload);
			},

			/**
			 * The cart to check out with, and the posted actions re-priced for `billingPeriod`. Line item prices come
			 * from the server, which is the only thing that knows what one period of a subscription costs.
			 * @param cartActions {CartAction[]}
			 * @param billingPeriod {('Monthly'|'Annual')?} the plan the customer picked, or null on the first load
			 * @return {Promise<{cart: ShoppingCart, cartActions: CartAction[], offersBillingPeriodChoice: boolean}>}
			 */
			getCartForCheckout(cartActions, billingPeriod) {
				return this.post('api/cart-for-checkout', {cartActions, billingPeriod: billingPeriod || null});
			},
			/**
			 * @param licenseKey {string}
			 * @param registeredTo {string}
			 * @param cartItemId {number}
			 * @return {Promise<OrderAndRenewPreview>}
			 */
			getOrderAndRenewPreview (licenseKey, registeredTo, cartItemId) {
				return this.get('api/order-and-renew-preview', {params:{licenseKey, registeredTo, cartItemId}});
			},

			checkout: function (cart) {
				return this.post('api/checkout', cart);
			},

			/**
			 * Starts a standalone Stripe 3DS authentication for a pending order, from a PaymentMethod the browser made.
			 * @param {{cartId:number, paymentMethodId:string, browser:Object}} payload
			 * @return {Promise<{authenticationId:string, status:string, fingerprintingUrl:?string, challengeUrl:?string}>}
			 */
			stripe3DSAuthenticate: function (payload) {
				return this.post('api/public/3ds-stripe-authenticate', payload);
			},

			/**
			 * Submits an authentication the issuer wanted fingerprinting for, with the browser's fingerprinting result.
			 * @param {{authenticationId:string, fingerprintingResult:?string}} payload
			 */
			stripe3DSSubmit: function (payload) {
				return this.post('api/public/3ds-stripe-submit', payload);
			},

			/**
			 * Completes the order once the Stripe 3DS authentication has finished: the server verifies it succeeded and
			 * re-submits to CardPointe with the cryptogram.
			 * @param {{authenticationId:string, cartId:number, cvv2:string}} payload
			 * @param payload {{ setupIntentId: string, cartId: number, cvv2: string }}
			 * @return {Promise<CheckoutResponse>}
			 */
			checkout3DSStripeComplete: function (payload) {
				return this.post('api/public/3ds-stripe-complete', payload);
			},

			generateQuote: function (cart) {
				return this.post('api/generate-quote', cart);
			},

			createAccount: function (promoCode, email, password) {
				return this.post('api/create-account', null, {params: {promoCode: promoCode, email: email, password: password}});
			},
			verifyAccount: function(payload) {
			    return this.post('api/verify-account', null, {params:payload});
			},

			/**
			 * @param payment
			 * @param licenseKey
			 * @return {Promise<UpdatePaymentInfoResult>}
			 */
			updatePaymentInfo: function (payment, licenseKey) {
				return this.post('api/update-payment-info', payment, {params: {licenseKey}});
			},

			currentUser: function() {
			    return this.get('api/current-user');
			},
			registerUser(registerData) {
				return this.post('api/auth-register-user', registerData);
			},
			/**
			 * Use `session.login` instead, which saves the resulting account in session.
			 * @param credentials {{email:string,password:string}} contains email / password
			 * @returns account promise
			 */
			login: function (credentials) {
				return this.post('api/login',  null, {headers:{'Authorization':'Basic ' + btoa(credentials.email + ':' + credentials.password)}});
			},

			requestLoginLink: function(email) {
				return this.post('api/request-login-link', { email: email });
			},

			verifyLoginLink: function(token) {
				return this.post('api/verify-login-link', { token: token });
			},


			/**
			 * Use `session.logout` instead, which updates the session before calling this method.
			 */
			logout: function() {
			    return this.post('api/logout');
			},
			getAccountTmpToken: function (token) {
				return this.get('api/account-tmp-token', {params: {token: token}});
			},

			/**
			 * @param licenseKey {String}
			 * @param registeredTo {String}
			 * @param productId {number?}
			 * @param upgradeTo {number?}
			 * @param rememberMe {boolean?} whether to write to localStorage
			 * @return {Promise<LicenseInfo>}
			 */
			getLicense: function (licenseKey, registeredTo, productId, upgradeTo, rememberMe) {
				return this.get('api/license', {params: {licenseKey: licenseKey, registeredTo: registeredTo, productId: productId, upgradeTo: upgradeTo}})
					.then(found => {
						updateOrClearLocalStorage(licenseKey, registeredTo, rememberMe);
						return found;
					});
			},

			linkLicense: function(licenseKey, registeredTo) {
				return this.post('api/link-license', { licenseKey: licenseKey, registeredTo: registeredTo });
			},

			getMyLicenses() {
				return this.get('api/my-licenses');
			},
			getProduct: function (code) {
				return this.get('api/product', {params: {code: code}});
			},

			getProductById: function (id) {
				return this.get('api/product', {params: {id: id}});
			},

			getRenewalCandidates: function (licenseKey, registeredTo, rememberMe=true) {
				return this.get('api/renewal-candidates', {params: {licenseKey: licenseKey, registeredTo: registeredTo}})
					.then(found=>{
						updateOrClearLocalStorage(licenseKey, registeredTo, rememberMe);
						return found;
					});
			},

			/**
			 * @return {UpgradeCandidates}
			 */
			getUpgradeCandidates: function (licenseKey, registeredTo, productId, rememberMe = true) {
				return this.get('api/upgrade-candidates', {params: {licenseKey: licenseKey, registeredTo: registeredTo, productId: productId}})
					.then(found => {
						updateOrClearLocalStorage(licenseKey, registeredTo, rememberMe);
						return found;
					});
			},

			getUpsells: function (productId) {
				return this.get('api/products-upsell', {params: {productId: productId}});
			},

			getProducts: function () {
				return this.get('api/products');
			},

			getProductsAll: function () {
				return this.get('api/products-all');
			},

			getProductVersions: function (productId) {
				return this.get('api/product-versions', {params:{productId}});
			},

			getProductVariant: function (id) {
				return this.get('api/product-variant', {params: {id: id}});
			},

			getProductVariantByProductId: function (productId, optionalVariantType) {
				return this.get('api/product-variant-by-product-id', {params: {productId: productId, variantType: optionalVariantType}});
			},

			getPromoCode: function (promoCode) {
				return this.get('api/promo-code', {params: {code: promoCode}});
			},

			getReceiptHtml: function (licenseKey, cartId) {
				return this.get('api/receipt-html', {params: {licenseKey: licenseKey, cartId: cartId}});
			},

			getServerInfo: function () {
				return this.get('api/server-info');
			},

			mirrorSyncPricing: function () {
				return this.get('api/mirrorsync-pricing');
			},

			postMirrorSyncCustomForm: function (custom) {
				return this.post('api/mirrorsync-custom-form', custom);
			},

			resetPassword: function (email) {
				return this.post('api/reset-password', null, {params: {email: email}});
			},
			resetPasswordPut: function (credentials) {
				return this.put('api/reset-password', null, {params: credentials});
			},
			getUpgradeVariants: function (versionId, variantTypeId) {
				return this.get('api/variant-upgrade-options', {params: {versionId: versionId, variantTypeId: variantTypeId}});
			},
			getCapabilities: function ({supportId}) {
				return this.get('api/admin/capabilities', {params: {supportId}});
			},


			/**
			 * @param licenseKey {string}
			 * @param registeredTo {string}
			 * @param cartItemIds {number[]}
			 * @param autoRenewEnabled {boolean}
			 * @return {MaintenanceSupport}
			 */
			toggleAutoRenewForCartItemIds: function (licenseKey, registeredTo, cartItemIds, autoRenewEnabled = true) {
				/**
				 * @type {ToggleAutoRenewInput}
				 */
				const payload = {
					autoRenewEnabled,
					licenseKey,
					registeredTo,
					cartItemIds
				};
				return this.post('api/toggle-auto-renew-for-cart-items', payload);
			},

			/**
			 * Switches an order between monthly and annual billing. Takes effect at the next renewal --
			 * nothing is charged or refunded now.
			 *
			 * @param licenseKey {string}
			 * @param registeredTo {string}
			 * @param cartItemIds {number[]}
			 * @param billingPeriod {'Monthly'|'Annual'}
			 * @return {ChangedLicense[]} each changed license with its next renewal repriced on the new plan
			 */
			changeBillingPeriod: function (licenseKey, registeredTo, cartItemIds, billingPeriod) {
				return this.post('api/change-billing-period', {
					licenseKey,
					registeredTo,
					cartItemIds,
					billingPeriod
				});
			},

			/*
			///////////////////////////////////////////////////////////////////////////
			// ADMIN STUFF
			///////////////////////////////////////////////////////////////////////////
			*/
			/**
			 * {@link changeBillingPeriod} for an admin, who needs no license key. The server applies the same billing rules.
			 *
			 * @param cartItemIds {number[]}
			 * @param billingPeriod {'Monthly'|'Annual'}
			 * @return {ChangedLicense[]}
			 */
			adminChangeBillingPeriod: function (cartItemIds, billingPeriod) {
				return this.post('api/admin/change-billing-period', {cartItemIds, billingPeriod});
			},
			saveOrder: function (order) {
				return this.post('api/admin/orders', order);
			},

			saveProduct: function (product) {
				return this.post('api/admin/product', product);
			},

			savePromoCode: function (promo) {
				return this.post('api/admin/promo-codes', promo);
			},

			adminVariantTypes: function () {
				return this.get('api/admin/variant-types');
			},

			adminCurrentUser: function () {
				return this.get('api/admin/current-user');
			},
			/**
			 * @param params {{userId:string}}
			 * @return {Promise<Account>}
			 */
			adminSwitchUser: function (params) {
				return this.post('api/admin/switch-user', null, {params});
			},
			adminFetchEmails: function (/** Number */ orderId) {
				return this.get('api/admin/fetch-emails',{params:{orderId}});
			},
			/**
			 * @param emails {string[]}
			 * @return {Promise<Object<string, 'SOFT'|'HARD'>>} how each blocked address is blocked; unblocked ones are absent
			 */
			adminFetchEmailBlocks: function (emails) {
				return this.get('api/admin/email-opt-out', {params: {email: emails}});
			},
			adminFetchUserByEmail: function (/** string */ email) {
				return this.get('api/admin/fetch-user',{params:{email}});
			},
			adminFetchLicenseCheckActivities: function (/** Number */ orderId) {
				return this.get('api/admin/fetch-license-check-activities',{params:{orderId}});
			},
			adminGenerateSupportStub: function (/** Number */ cartItemId) {
				return this.post('api/admin/generate-support-stub', null,{params:{cartItemId}});
			},
			adminRenewProcess: function (/*ShoppingCart*/ cart) {
				return this.post('api/admin/renew-process', null, {params: {orderId: cart.id}});
			},
			generateLicenseKey: function (/**GenerateLicenseKeyPayload*/payload) {
				return this.post('api/admin/generate-license-key', payload);
			},
			generateLicenseKeyForOrderId: function (/**number*/orderId) {
				return this.get('api/admin/generate-license-key-for-order-id', {params: {orderId}});
			},
			parseLicenseKey: function (/**string*/ licenseKey) {
				return this.get('api/admin/parse-license-key', {params: {licenseKey}});
			},

			/*
			///////////////////////////////////////////////////////////////////////////
			// REPORTING
			///////////////////////////////////////////////////////////////////////////
			*/
			runReport: function (reportName, args) {
				return this.get('api/reports/' + reportName, args);
			},
			/**
			 * @param reportName {string}
			 * @return {Promise<DynamicReport>}
			 */
			dynamicReportGet: function (reportName) {
				return this.get('api/reports/dynamic-report', {params: {reportName: reportName}});
			},
			/**
			 * @param reportName {string}
			 * @param params {Record<string,any>}
			 * @param reportFormat {ReportFormat}
			 * @return {*}
			 */
			dynamicReportPost: function (reportName, params, reportFormat) {
				/**
				 * @type {DynamicReportRequest}
				 */
				let dynamicReportExecutePayload = {params, reportFormat, reportName:reportName};
				return this.post('api/reports/dynamic-report', dynamicReportExecutePayload, {params: {reportName: reportName}});
			}


		};

	});
